import type { Node, Edge } from '@xyflow/react'
import type { SavedNode } from '@/components/canvas/utils/coord-converter'
import type { SerializedEdge } from '@/lib/api/canvas-types'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'
import {
  filterNodesTransientState,
  injectSeriesDataForSave,
  stripTransientStateFields,
} from '@/utils/canvas/nodeStateFilter'

/**
 * Canvas content snapshot — cheap change detection for state-driven autosave.
 *
 * Each node/edge is reduced to a comparison key: its JSON with view-only
 * fields (selection, drag/resize flags, measured dimensions) and transient
 * execution state (generationStatus, progress, locks…) removed. Keys are cached
 * per object reference, so a state update that replaces only a few nodes only
 * serializes those nodes. Canvas state is immutable (React Flow change helpers,
 * Immer), which is what makes reference caching valid.
 */
export interface CanvasContentSnapshot {
  nodes: string[]
  edges: string[]
}

/** Top-level React Flow node fields that never affect what is persisted meaningfully */
const NODE_VIEW_FIELDS = new Set(['selected', 'dragging', 'resizing', 'measured'])
/** Top-level React Flow edge fields that are view-only */
const EDGE_VIEW_FIELDS = new Set(['selected'])

const nodeKeyCache = new WeakMap<Node, string>()
const edgeKeyCache = new WeakMap<Edge, string>()
const seriesKeyCache = new WeakMap<object, string>()

function omitFields(source: object, fields: Set<string>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(source)) {
    if (!fields.has(key)) out[key] = value
  }
  return out
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function normalizeNodeData(data: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...data }
  const state = asRecord(data.state)
  if (state) normalized.state = stripTransientStateFields(state)
  const component = asRecord(data.component)
  const componentState = asRecord(component?.state)
  if (component && componentState) {
    normalized.component = { ...component, state: stripTransientStateFields(componentState) }
  }
  return normalized
}

/**
 * Reduce a node — live, or in the shape the server stores — to what a save
 * actually persists. Exported because the conflict merge has to ask the same
 * question the autosave asks ("did this node really change?"): comparing raw
 * JSON there would read a measurement or a selection as a user edit and let it
 * outrank another tab's real change.
 */
export function normalizeNodeForCompare(node: object): Record<string, unknown> {
  const rest = omitFields(node, NODE_VIEW_FIELDS)
  const data = asRecord((node as { data?: unknown }).data)
  if (data) rest.data = normalizeNodeData(data)
  return rest
}

/** Edge counterpart of `normalizeNodeForCompare` */
export function normalizeEdgeForCompare(edge: object): Record<string, unknown> {
  return omitFields(edge, EDGE_VIEW_FIELDS)
}

function nodeCompareKey(node: Node): string {
  const cached = nodeKeyCache.get(node)
  if (cached !== undefined) return cached

  const key = JSON.stringify(normalizeNodeForCompare(node))
  nodeKeyCache.set(node, key)
  return key
}

function edgeCompareKey(edge: Edge): string {
  const cached = edgeKeyCache.get(edge)
  if (cached !== undefined) return cached
  const key = JSON.stringify(normalizeEdgeForCompare(edge))
  edgeKeyCache.set(edge, key)
  return key
}

function seriesArrayKey(items: object[] | undefined): string {
  if (!items) return ''
  const cached = seriesKeyCache.get(items)
  if (cached !== undefined) return cached
  const key = JSON.stringify(items)
  seriesKeyCache.set(items, key)
  return key
}

/**
 * Large series data (variations, composed prompts) lives outside node state
 * but is injected into nodes on save, so it is part of the content.
 */
function seriesSuffix(nodeId: string): string {
  const { variations, composedPrompts } = useSeriesDataStore.getState()
  const v = variations.get(nodeId)
  const p = composedPrompts.get(nodeId)
  if (!v && !p) return ''
  return `|series:${seriesArrayKey(v)}|${seriesArrayKey(p)}`
}

/**
 * Build a comparison snapshot of canvas content
 */
export function buildContentSnapshot(nodes: Node[], edges: Edge[]): CanvasContentSnapshot {
  return {
    nodes: nodes.map(node => {
      const suffix = seriesSuffix(node.id)
      const key = nodeCompareKey(node)
      return suffix ? key + suffix : key
    }),
    edges: edges.map(edgeCompareKey),
  }
}

function keysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Check whether two content snapshots represent the same persisted content
 */
export function contentSnapshotsEqual(
  a: CanvasContentSnapshot | null | undefined,
  b: CanvasContentSnapshot | null | undefined,
): boolean {
  if (!a || !b) return a === b
  return keysEqual(a.nodes, b.nodes) && keysEqual(a.edges, b.edges)
}

/**
 * Build the API payload for a page content save (full node/edge replacement).
 * Nodes are cleaned of transient state and re-injected with series data.
 * React Flow nodes/edges are cast at the serialization boundary: they carry the
 * same data as SavedNode/SerializedEdge once serialized to JSON.
 */
export function buildContentPayload(
  nodes: Node[],
  edges: Edge[],
): { nodes: SavedNode[]; edges: SerializedEdge[] } {
  return {
    nodes: injectSeriesDataForSave(filterNodesTransientState(nodes)) as unknown as SavedNode[],
    edges: edges as unknown as SerializedEdge[],
  }
}
